import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ImagePlus, Send, Sparkles } from 'lucide-react';
import {
  bannersApi,
  companyApi,
  type BannerCreateInput,
  type BannerLinkType,
  type BannerTargetApp,
  type BannerUpdateInput,
} from '@/services/api';
import {
  PageHeader,
  Section,
  TextInput,
  Select,
  LoadingState,
  ErrorState,
} from '@/components/common';
import { BANNER_FONTS, ensureBannerFont } from '@/utils/bannerFonts';

interface FormState {
  targetApp: BannerTargetApp;
  title: string;
  fileUrl: string;
  linkType: BannerLinkType;
  linkUrl: string;
  sortOrder: string;
  isUse: boolean;
  /** 게시 시작 — 일자/시/분 분리 입력. 일자 비어있으면 시작 제한 없음 */
  displayStartDate: string;
  displayStartHour: string;
  displayStartMinute: string;
  /** 게시 종료 — 일자/시/분 분리 입력. 일자 비어있으면 종료 제한 없음 */
  displayEndDate: string;
  displayEndHour: string;
  displayEndMinute: string;
  /** 위치기반 노출 */
  isLocationBased: boolean;
  companySeq: string;
  radiusKm: string;
  /** AI 오버레이(표시-시점 합성) */
  isOverlay: boolean;
  headline: string;
  subline: string;
  textZone: string;
  pointColor: string;
  fontKey: string;
}

const DEFAULT_START_HOUR = '00';
const DEFAULT_START_MINUTE = '00';
const DEFAULT_END_HOUR = '23';
const DEFAULT_END_MINUTE = '59';

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, '0'),
);
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, '0'),
);

const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 백엔드 제한과 동일 (5MB)

function splitDateTime(value: string | null | undefined): {
  date: string;
  hour: string;
  minute: string;
} {
  if (!value) return { date: '', hour: '', minute: '' };
  const [datePart = '', timePart = ''] = value.split(' ');
  const [hh = '', mm = ''] = timePart.split(':');
  return { date: datePart, hour: hh, minute: mm };
}

function joinDateTime(
  date: string,
  hour: string,
  minute: string,
  defaults: { hour: string; minute: string },
): string | undefined {
  if (!date) return undefined;
  const hh = (hour || defaults.hour).padStart(2, '0');
  const mm = (minute || defaults.minute).padStart(2, '0');
  return `${date} ${hh}:${mm}:00`;
}

const TARGET_OPTIONS: { value: BannerTargetApp; label: string }[] = [
  { value: 'GUARDIAN', label: '보호자앱' },
  { value: 'BUSINESS', label: '비즈니스앱' },
];

const LINK_TYPE_OPTIONS: {
  value: BannerLinkType;
  label: string;
  help: string;
}[] = [
  { value: 'NONE', label: '이동 없음', help: '클릭해도 이동하지 않습니다.' },
  {
    value: 'INTERNAL',
    label: '앱 내 이동',
    help: '예: /customer/layout/home-serach-detail?companySeq=1',
  },
  { value: 'EXTERNAL', label: '외부 링크', help: '예: https://mungtoday.com/event' },
];

interface Props {
  mode?: 'create' | 'edit';
}

export function BannerCreatePage({ mode = 'create' }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams<{ id?: string }>();
  const editingId = mode === 'edit' && params.id ? Number(params.id) : null;
  const isEdit = editingId != null;

  const fileInputRef = useRef<HTMLInputElement>(null);
  /** 선택만 해두고 실제 업로드는 "배너 등록/수정" 제출 시점에 수행 */
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [form, setForm] = useState<FormState>({
    targetApp: 'GUARDIAN',
    title: '',
    fileUrl: '',
    linkType: 'NONE',
    linkUrl: '',
    sortOrder: '0',
    isUse: true,
    displayStartDate: '',
    displayStartHour: DEFAULT_START_HOUR,
    displayStartMinute: DEFAULT_START_MINUTE,
    displayEndDate: '',
    displayEndHour: DEFAULT_END_HOUR,
    displayEndMinute: DEFAULT_END_MINUTE,
    isLocationBased: false,
    companySeq: '',
    radiusKm: '10',
    isOverlay: false,
    headline: '',
    subline: '',
    textZone: 'bottom-left',
    pointColor: '#FF9934',
    fontKey: 'gothic',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // AI 배너 자동 생성 상태
  const [aiProgress, setAiProgress] = useState<number | null>(null); // null=대기
  const [aiStep, setAiStep] = useState('');
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiInfo, setAiInfo] = useState<string | null>(null); // 생성 결과 요약(소스·엔진)

  // 위치기반 노출 연결 매장 후보 (드롭다운용)
  const { data: companyPage } = useQuery({
    queryKey: ['bannerCompanyOptions'],
    queryFn: () => companyApi.getCompanies({ size: 500 }),
    staleTime: 5 * 60 * 1000,
  });
  const companyOptions = companyPage?.content ?? [];

  const {
    data: existing,
    isLoading: isLoadingDetail,
    error: detailError,
    refetch,
  } = useQuery({
    queryKey: ['banner', editingId],
    queryFn: () => bannersApi.getBannerDetail(editingId as number),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!isEdit || !existing) return;
    const start = splitDateTime(existing.displayStartAt);
    const end = splitDateTime(existing.displayEndAt);
    setForm({
      targetApp: existing.targetApp,
      title: existing.title ?? '',
      fileUrl: existing.fileUrl,
      linkType: existing.linkType,
      linkUrl: existing.linkUrl ?? '',
      sortOrder: String(existing.sortOrder ?? 0),
      isUse: existing.isUse,
      displayStartDate: start.date,
      displayStartHour: start.hour || DEFAULT_START_HOUR,
      displayStartMinute: start.minute || DEFAULT_START_MINUTE,
      displayEndDate: end.date,
      displayEndHour: end.hour || DEFAULT_END_HOUR,
      displayEndMinute: end.minute || DEFAULT_END_MINUTE,
      isLocationBased: existing.isLocationBased ?? false,
      companySeq: existing.companySeq != null ? String(existing.companySeq) : '',
      radiusKm: existing.radiusKm != null ? String(existing.radiusKm) : '10',
      isOverlay: existing.isOverlay ?? false,
      headline: existing.headline ?? '',
      subline: existing.subline ?? '',
      textZone: existing.textZone ?? 'bottom-left',
      pointColor: existing.pointColor ?? '#FF9934',
      fontKey: existing.fontKey ?? 'gothic',
    });
  }, [isEdit, existing]);

  const clearError = (name: keyof FormState) => {
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleTextChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    clearError(name as keyof FormState);
  };

  // 로컬 미리보기 URL — 새 파일을 고르면 기존 objectURL 은 해제
  const localPreviewUrl = useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : null),
    [imageFile],
  );
  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);
  /** 미리보기: 새로 고른 파일 우선, 없으면 기존 저장된 URL(수정 모드) */
  const previewUrl = localPreviewUrl ?? form.fileUrl;

  const handleImageSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // 같은 파일 재선택도 가능하도록 input 값을 비워둔다
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      alert('이미지는 5MB 이하만 업로드할 수 있습니다.');
      return;
    }
    setImageFile(file);
    // 직접 이미지를 고르면 AI 오버레이 해제(순수 이미지 배너로)
    setForm(prev => ({ ...prev, isOverlay: false }));
    clearError('fileUrl');
  };

  /**
   * [AI 배너 생성] — 비동기 job 시작 후 진척률을 폴링해 표시.
   * 서버가 매장 사진(홍보→사진첩→무사진 텍스트)을 취합·Gemini 분석 → 결과를 오버레이 배너로 폼에 채운다.
   * 비동기라 관리자 10초 타임아웃에 걸리지 않는다.
   */
  const handleAiGenerate = async () => {
    const seq = Number(form.companySeq);
    if (!seq) {
      setAiError('먼저 연결 매장을 선택해주세요.');
      return;
    }
    setAiError(null);
    setAiInfo(null);
    setAiProgress(8);
    setAiStep('매장 사진 취합 중');

    const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
    try {
      const started = await bannersApi.aiGenerateAsync(seq);
      if (!started?.jobId) throw new Error('생성을 시작하지 못했습니다.');

      // 진척률 폴링(최대 90초). 대기 중에도 바가 멈춰보이지 않게 완만히 이징(완료 전 96% 상한).
      let shown = started.progress || 8;
      let final = started;
      const deadline = Date.now() + 90_000;
      while (Date.now() < deadline) {
        const st = await bannersApi.aiGenerateStatus(started.jobId);
        final = st;
        setAiStep(st.step || '분석 중');
        const target = st.done ? 100 : Math.min(st.progress, 96);
        shown = Math.max(shown, Math.min(target, shown + 6));
        setAiProgress(st.done ? 100 : shown);
        if (st.done || st.failed) break;
        await sleep(700);
      }

      if (final.failed) throw new Error(final.error || 'AI 배너 생성에 실패했습니다.');
      const result = final.result;
      if (!final.done || !result) {
        throw new Error('생성이 시간 내에 끝나지 않았습니다. 다시 시도해주세요.');
      }

      setAiProgress(100);
      setAiStep('완료');
      setImageFile(null); // AI가 준 결과 사용 → 로컬 선택 해제

      // 히어로 사진이 없으면(무사진 매장) 색배경 + 문구만인 텍스트-only 오버레이 배너로 생성
      setForm(prev => ({
        ...prev,
        fileUrl: result.heroUrl ?? '',
        isOverlay: true,
        headline: result.headline ?? prev.headline,
        subline: result.subline ?? '',
        textZone: result.textZone ?? 'bottom-left',
        pointColor: result.dominantColor ?? '#FF9934',
        fontKey: result.fontKey ?? 'gothic',
        // AI 매장 배너는 해당 매장 주변 노출 + 매장 상세로 이동이 기본
        isLocationBased: true,
        companySeq: String(seq),
        linkType: 'INTERNAL' as BannerLinkType,
        linkUrl: `/customer/layout/home-serach-detail?companySeq=${seq}`,
      }));
      clearError('fileUrl');

      const srcLabel =
        result.source === 'WORKPLACE'
          ? `홍보사진 ${result.mediaCount}장 분석`
          : result.source === 'ALBUM'
            ? `사진첩 ${result.mediaCount}장 분석`
            : '사진이 없어 매장 정보로 생성';
      const engine = result.aiAnalyzed ? 'AI 분석' : '규칙기반(AI 미가동)';
      setAiInfo(`${srcLabel} · ${engine}`);
      window.setTimeout(() => setAiProgress(null), 900);
    } catch (e) {
      setAiProgress(null);
      setAiError(e instanceof Error ? e.message : 'AI 배너 생성에 실패했습니다.');
    }
  };

  const buildDisplayStart = (): string | undefined =>
    joinDateTime(form.displayStartDate, form.displayStartHour, form.displayStartMinute, {
      hour: DEFAULT_START_HOUR,
      minute: DEFAULT_START_MINUTE,
    });
  const buildDisplayEnd = (): string | undefined =>
    joinDateTime(form.displayEndDate, form.displayEndHour, form.displayEndMinute, {
      hour: DEFAULT_END_HOUR,
      minute: DEFAULT_END_MINUTE,
    });

  const validate = (): boolean => {
    const next: typeof errors = {};
    // 텍스트-only 오버레이 배너(무사진)는 이미지 없이 허용
    if (!imageFile && !form.fileUrl && !form.isOverlay)
      next.fileUrl = '배너 이미지를 선택하거나 AI로 생성해주세요.';
    if (form.linkType !== 'NONE' && !form.linkUrl.trim()) {
      next.linkUrl = '이동 경로를 입력해주세요.';
    }
    if (form.linkType === 'EXTERNAL' && form.linkUrl.trim() && !/^https?:\/\//.test(form.linkUrl.trim())) {
      next.linkUrl = '외부 링크는 http:// 또는 https:// 로 시작해야 합니다.';
    }
    if (form.linkType === 'INTERNAL' && form.linkUrl.trim() && !form.linkUrl.trim().startsWith('/')) {
      next.linkUrl = '앱 내 이동 경로는 / 로 시작해야 합니다.';
    }
    if (form.sortOrder !== '' && !/^-?\d+$/.test(form.sortOrder.trim())) {
      next.sortOrder = '순서는 숫자로 입력해주세요.';
    }
    const start = buildDisplayStart();
    const end = buildDisplayEnd();
    if (start && end && start > end) {
      next.displayEndDate = '종료 일시는 시작 일시 이후여야 합니다.';
    }
    if (form.isLocationBased && !form.companySeq) {
      next.companySeq = '위치기반 노출은 연결 매장을 선택해야 합니다.';
    }
    if (form.isLocationBased && form.radiusKm.trim() && !/^\d+$/.test(form.radiusKm.trim())) {
      next.radiusKm = '반경은 숫자로 입력해주세요.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      // 새 이미지를 골랐으면 이 시점(등록 클릭)에 업로드
      let fileUrl = form.fileUrl;
      if (imageFile) {
        const uploaded = await bannersApi.uploadImage(imageFile);
        // 백엔드 ServiceResult 는 실패도 HTTP 200 + data:null 로 내려올 수 있음
        if (!uploaded?.url) {
          alert('이미지 업로드에 실패했습니다. 파일 형식/용량을 확인해주세요.');
          return;
        }
        fileUrl = uploaded.url;
      }

      const start = buildDisplayStart();
      const end = buildDisplayEnd();
      const sortOrder = Number(form.sortOrder.trim() || '0');
      const linkUrl =
        form.linkType === 'NONE' ? undefined : form.linkUrl.trim();
      const locationFields = {
        isLocationBased: form.isLocationBased,
        companySeq: form.isLocationBased ? Number(form.companySeq) : null,
        radiusKm: form.isLocationBased ? Number(form.radiusKm.trim() || '10') : null,
      };
      const overlayFields = {
        isOverlay: form.isOverlay,
        headline: form.isOverlay ? form.headline.trim() || null : null,
        subline: form.isOverlay ? form.subline.trim() || null : null,
        textZone: form.isOverlay ? form.textZone || 'bottom-left' : null,
        pointColor: form.isOverlay ? form.pointColor || '#FF9934' : null,
        fontKey: form.isOverlay ? form.fontKey || 'gothic' : null,
      };

      if (isEdit && editingId != null) {
        // displayStartAt/EndAt 가 undefined면 "변경 없음" — 비우려면 clear* 플래그 사용
        const payload: BannerUpdateInput = {
          targetApp: form.targetApp,
          title: form.title.trim(),
          fileUrl,
          linkType: form.linkType,
          linkUrl,
          sortOrder,
          isUse: form.isUse,
          ...locationFields,
          ...overlayFields,
        };
        if (start) {
          payload.displayStartAt = start;
        } else {
          payload.clearDisplayStart = true;
        }
        if (end) {
          payload.displayEndAt = end;
        } else {
          payload.clearDisplayEnd = true;
        }
        await bannersApi.updateBanner(editingId, payload);
        // 목록/상세 캐시 무효화 — 리스트로 돌아갔을 때 수정 내용이 바로 보이도록
        await queryClient.invalidateQueries({ queryKey: ['banners'] });
        await queryClient.invalidateQueries({ queryKey: ['banner', editingId] });
        alert('배너가 수정되었습니다.');
      } else {
        const payload: BannerCreateInput = {
          targetApp: form.targetApp,
          title: form.title.trim(),
          fileUrl,
          linkType: form.linkType,
          linkUrl,
          sortOrder,
          isUse: form.isUse,
          displayStartAt: start ?? null,
          displayEndAt: end ?? null,
          ...locationFields,
          ...overlayFields,
        };
        await bannersApi.createBanner(payload);
        // 목록 캐시 무효화 — 리스트로 돌아갔을 때 새 배너가 바로 보이도록
        await queryClient.invalidateQueries({ queryKey: ['banners'] });
        alert('배너가 등록되었습니다.');
      }
      navigate('/banners');
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : isEdit
            ? '배너 수정에 실패했습니다.'
            : '배너 등록에 실패했습니다.';
      alert(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEdit && isLoadingDetail) {
    return <LoadingState />;
  }
  if (isEdit && detailError) {
    return <ErrorState error={detailError} onRetry={() => refetch()} />;
  }

  const selectedLinkOption = LINK_TYPE_OPTIONS.find(
    o => o.value === form.linkType,
  );

  // 매장 연결 위치배너는 수정 시 위치 노출을 해제할 수 없음(문구·링크가 매장 전용이라 전역 전환 시 오노출)
  const lockLocation =
    isEdit && existing?.isLocationBased === true && existing?.companySeq != null;

  return (
    <div className="space-y-4">
      <PageHeader
        title={isEdit ? '배너 수정' : '배너 등록'}
        subtitle={
          isEdit
            ? '등록된 홈 배너를 수정합니다.'
            : '보호자앱 홈 상단에 노출할 배너를 등록합니다.'
        }
        right={
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft size={16} />
            뒤로가기
          </button>
        }
      />

      <form onSubmit={handleSubmit} className="max-w-3xl space-y-4">
        <Section title="배너 정보">
          <div className="space-y-4">
            <Field label="대상 앱" required>
              <div className="flex flex-wrap gap-3">
                {TARGET_OPTIONS.map(opt => {
                  const checked = form.targetApp === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                        checked
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      } ${isSubmitting ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                      <input
                        type="radio"
                        name="targetApp"
                        value={opt.value}
                        checked={checked}
                        onChange={() =>
                          setForm(prev => ({ ...prev, targetApp: opt.value }))
                        }
                        disabled={isSubmitting}
                        className="accent-primary-500"
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </Field>

            <Field
              label="배너 이미지"
              required
              error={errors.fileUrl}
              help="권장 사이즈 750×352px (2x 해상도, 5MB 이하). 위치기반 노출을 켜고 매장을 고르면 [AI 배너 생성]으로 자동 제작할 수 있고, 아니면 직접 이미지를 올리세요. 이미지는 하단의 등록/수정 버튼을 눌러야 서버에 저장됩니다."
            >
              <div className="space-y-2">
                {(previewUrl || form.isOverlay) &&
                  (form.isOverlay ? (
                    <BannerOverlayPreview
                      imageUrl={previewUrl || ''}
                      headline={form.headline}
                      subline={form.subline}
                      textZone={form.textZone}
                      pointColor={form.pointColor}
                      fontKey={form.fontKey}
                    />
                  ) : (
                    <img
                      src={previewUrl}
                      alt="배너 미리보기"
                      className="aspect-[750/352] w-full max-w-md rounded-xl object-cover ring-1 ring-gray-200"
                    />
                  ))}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <ImagePlus size={16} />
                    {previewUrl ? '이미지 변경' : '이미지 선택'}
                  </button>
                  {previewUrl && (
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={form.isOverlay}
                        onChange={e =>
                          setForm(prev => ({ ...prev, isOverlay: e.target.checked }))
                        }
                        disabled={isSubmitting}
                        className="h-4 w-4 accent-primary-500"
                      />
                      이미지 위 문구 오버레이 사용
                    </label>
                  )}
                </div>
              </div>
            </Field>

            {form.isOverlay && (
              <Field
                label="오버레이 문구"
                help="AI가 채운 문구를 그대로 쓰거나 수정하세요. 앱에서는 이미지 위에 이 문구가 얹혀 노출됩니다."
              >
                <div className="space-y-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      헤드라인 (매장명)
                    </label>
                    <TextInput
                      name="headline"
                      value={form.headline}
                      onChange={handleTextChange}
                      placeholder="예: 뽀댕유치원 애견호텔"
                      disabled={isSubmitting}
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      서브라인 (지역·서비스)
                    </label>
                    <TextInput
                      name="subline"
                      value={form.subline}
                      onChange={handleTextChange}
                      placeholder="예: 강남구 · 유치원/호텔/미용"
                      disabled={isSubmitting}
                      maxLength={150}
                    />
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        문구 위치
                      </label>
                      <Select
                        name="textZone"
                        value={form.textZone}
                        onChange={handleTextChange}
                        disabled={isSubmitting}
                        className="w-40"
                      >
                        <option value="bottom-left">좌하단</option>
                        <option value="bottom-right">우하단</option>
                        <option value="top-left">좌상단</option>
                        <option value="top-right">우상단</option>
                      </Select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        포인트 색
                      </label>
                      <input
                        type="color"
                        value={form.pointColor}
                        onChange={e =>
                          setForm(prev => ({ ...prev, pointColor: e.target.value }))
                        }
                        disabled={isSubmitting}
                        className="h-9 w-14 cursor-pointer rounded border border-gray-300"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        폰트
                      </label>
                      <Select
                        name="fontKey"
                        value={form.fontKey}
                        onChange={handleTextChange}
                        disabled={isSubmitting}
                        className="w-52"
                      >
                        {BANNER_FONTS.map(f => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                </div>
              </Field>
            )}

            <Field
              label="제목"
              help="목록 관리용이며, 이미지 대체 텍스트(alt)로도 사용됩니다."
            >
              <TextInput
                name="title"
                value={form.title}
                onChange={handleTextChange}
                placeholder="배너 제목을 입력하세요 (선택)"
                disabled={isSubmitting}
                maxLength={100}
              />
            </Field>

            <Field
              label="클릭 동작"
              error={errors.linkUrl}
              help={
                form.isLocationBased
                  ? '위치 배너는 연결 매장 상세페이지로 이동합니다. 매장을 선택하면 자동으로 채워지며 수정할 수 없습니다.'
                  : selectedLinkOption?.help
              }
            >
              <div className="flex flex-col gap-2 sm:flex-row">
                <Select
                  name="linkType"
                  value={form.linkType}
                  onChange={handleTextChange}
                  disabled={isSubmitting || form.isLocationBased}
                  className="sm:w-40"
                >
                  {LINK_TYPE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                {form.linkType !== 'NONE' && (
                  <TextInput
                    name="linkUrl"
                    value={form.linkUrl}
                    onChange={handleTextChange}
                    placeholder={
                      form.isLocationBased
                        ? '매장 선택 시 자동 입력'
                        : form.linkType === 'INTERNAL'
                          ? '/customer/layout/...'
                          : 'https://...'
                    }
                    disabled={isSubmitting || form.isLocationBased}
                    className="flex-1"
                    maxLength={500}
                  />
                )}
              </div>
            </Field>

            <Field
              label="노출 순서"
              error={errors.sortOrder}
              help="숫자가 작을수록 앞에 노출됩니다. 같은 순서면 최신 등록이 먼저 노출됩니다."
            >
              <TextInput
                name="sortOrder"
                value={form.sortOrder}
                onChange={handleTextChange}
                disabled={isSubmitting}
                className="max-w-[120px]"
                inputMode="numeric"
              />
            </Field>

            <Field
              label="게시 기간"
              error={errors.displayEndDate}
              help="비워두면 즉시 노출 / 무기한 유지됩니다. 시·분을 비우면 시작은 00:00, 종료는 23:59로 적용됩니다."
            >
              <div className="space-y-2">
                <DateTimeRow
                  label="시작"
                  dateName="displayStartDate"
                  hourName="displayStartHour"
                  minuteName="displayStartMinute"
                  dateValue={form.displayStartDate}
                  hourValue={form.displayStartHour}
                  minuteValue={form.displayStartMinute}
                  onChange={handleTextChange}
                  disabled={isSubmitting}
                />
                <DateTimeRow
                  label="종료"
                  dateName="displayEndDate"
                  hourName="displayEndHour"
                  minuteName="displayEndMinute"
                  dateValue={form.displayEndDate}
                  hourValue={form.displayEndHour}
                  minuteValue={form.displayEndMinute}
                  onChange={handleTextChange}
                  disabled={isSubmitting}
                />
              </div>
            </Field>

            <Field label="사용 여부">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.isUse}
                  onChange={e =>
                    setForm(prev => ({ ...prev, isUse: e.target.checked }))
                  }
                  disabled={isSubmitting}
                  className="h-4 w-4 accent-primary-500"
                />
                사용 (체크 해제 시 앱에 노출되지 않습니다)
              </label>
            </Field>

            <Field
              label="위치 기반 노출"
              error={errors.companySeq}
              help={
                lockLocation
                  ? '매장 연결 배너는 위치 노출을 해제할 수 없습니다(문구·링크가 매장 전용). 전역 배너는 새로 등록하세요.'
                  : '체크하면 연결한 매장 반경 안의 보호자에게만 노출됩니다. 체크 안 하면 기존처럼 전역 노출.'
              }
            >
              <label
                className={`inline-flex items-center gap-2 text-sm text-gray-700 ${
                  lockLocation ? 'cursor-not-allowed opacity-70' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.isLocationBased}
                  onChange={e => {
                    const on = e.target.checked;
                    setForm(prev => ({
                      ...prev,
                      isLocationBased: on,
                      // 위치기반이면 클릭 동작은 매장 상세로 자동(비활성). 매장 있으면 경로도 즉시 채움
                      ...(on
                        ? {
                            linkType: 'INTERNAL' as BannerLinkType,
                            linkUrl: prev.companySeq
                              ? `/customer/layout/home-serach-detail?companySeq=${prev.companySeq}`
                              : prev.linkUrl,
                          }
                        : {}),
                    }));
                  }}
                  disabled={isSubmitting || lockLocation}
                  className="h-4 w-4 accent-primary-500"
                />
                위치 기반으로 노출 (연결 매장 주변에만)
              </label>

              {form.isLocationBased && (
                <div className="mt-3 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">
                      연결 매장 <span className="text-red-500">*</span>
                    </label>
                    <Select
                      name="companySeq"
                      value={form.companySeq}
                      onChange={e => {
                        const seq = e.target.value;
                        setForm(prev => ({
                          ...prev,
                          companySeq: seq,
                          // 매장 선택 시 앱내이동을 해당 매장 상세페이지로 자동 설정
                          ...(seq
                            ? {
                                linkType: 'INTERNAL' as BannerLinkType,
                                linkUrl: `/customer/layout/home-serach-detail?companySeq=${seq}`,
                              }
                            : {}),
                        }));
                        setAiError(null);
                        clearError('companySeq');
                      }}
                      disabled={isSubmitting || aiProgress != null}
                      className="w-full"
                    >
                      <option value="">매장 선택</option>
                      {companyOptions.map(c => (
                        <option key={c.companySeq} value={c.companySeq}>
                          {c.companyName} (#{c.companySeq})
                        </option>
                      ))}
                    </Select>
                    <p className="mt-1 text-xs text-gray-500">
                      선택하면 클릭 시 이 매장의 상세페이지(내용·이용권 확인)로 이동하도록 자동 설정됩니다.
                    </p>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">
                      노출 반경 (km)
                    </label>
                    <TextInput
                      name="radiusKm"
                      value={form.radiusKm}
                      onChange={handleTextChange}
                      disabled={isSubmitting}
                      className="max-w-[120px]"
                      inputMode="numeric"
                      placeholder="10"
                    />
                    {errors.radiusKm && (
                      <p className="mt-1 text-xs text-red-600">{errors.radiusKm}</p>
                    )}
                  </div>

                  {/* AI 배너 자동 생성 — 선택한 매장 기준 */}
                  <div className="space-y-2 border-t border-gray-200 pt-3">
                    <button
                      type="button"
                      onClick={handleAiGenerate}
                      disabled={isSubmitting || aiProgress != null || !form.companySeq}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Sparkles size={16} />
                      {aiProgress != null ? '생성 중...' : 'AI 배너 생성'}
                    </button>
                    <p className="text-xs text-gray-500">
                      선택한 매장의 사진(홍보→사진첩)을 AI가 분석해 배너를 자동 제작합니다. 사진이 없으면
                      매장 정보로 생성하며, 문구는 아래에서 수정할 수 있어요.
                    </p>
                    {aiProgress != null && (
                      <div className="space-y-1">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                          <div
                            className="h-full rounded-full bg-primary-500 transition-all duration-200"
                            style={{ width: `${aiProgress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-600">
                          {aiStep} ({aiProgress}%)
                        </p>
                      </div>
                    )}
                    {aiError && <p className="text-xs text-red-600">{aiError}</p>}
                    {aiInfo && !aiError && aiProgress == null && (
                      <p className="text-xs font-medium text-primary-700">✓ {aiInfo}</p>
                    )}
                  </div>
                </div>
              )}
            </Field>
          </div>
        </Section>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Send size={16} />
            {isSubmitting
              ? isEdit
                ? '수정 중...'
                : '등록 중...'
              : isEdit
                ? '배너 수정'
                : '배너 등록'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * 오버레이 배너 미리보기 — 보호자앱이 렌더하는 것과 동일한 레이아웃
 * (히어로 이미지 + 스크림 그라디언트 + 문구/배지). 앱과 마크업을 맞춰야 미리보기가 정확.
 */
interface BannerOverlayPreviewProps {
  imageUrl: string;
  headline: string;
  subline: string;
  textZone: string;
  pointColor: string;
  fontKey: string;
}
function BannerOverlayPreview({
  imageUrl,
  headline,
  subline,
  textZone,
  pointColor,
  fontKey,
}: BannerOverlayPreviewProps) {
  const zone = textZone || 'bottom-left';
  const isTop = zone.includes('top');
  const isRight = zone.includes('right');
  const point = pointColor || '#FF9934';
  const fontFamily = ensureBannerFont(fontKey);
  return (
    <div className="relative aspect-[750/352] w-full max-w-md overflow-hidden rounded-xl ring-1 ring-gray-200">
      {imageUrl ? (
        <img src={imageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        // 무사진(텍스트-only): 주조색 그라디언트 배경
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(135deg, ${point} 0%, #1f2937 100%)` }}
        />
      )}
      <div
        className={`absolute inset-0 ${isTop ? 'bg-gradient-to-b' : 'bg-gradient-to-t'} from-black/70 via-black/25 to-transparent`}
      />
      <div
        className={`absolute inset-0 flex flex-col ${isTop ? 'justify-start' : 'justify-end'} ${isRight ? 'items-end text-right' : 'items-start text-left'} gap-1 p-4`}
      >
        <span
          className="mb-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
          style={{ backgroundColor: point }}
        >
          우리 동네 매장
        </span>
        <span
          className="text-lg font-extrabold leading-tight text-white drop-shadow-md line-clamp-2"
          style={{ fontFamily }}
        >
          {headline || '매장명'}
        </span>
        {subline && (
          <span
            className="text-xs font-medium text-white/90 drop-shadow line-clamp-1"
            style={{ fontFamily }}
          >
            {subline}
          </span>
        )}
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  help?: string;
  children: React.ReactNode;
}
function Field({ label, required, error, help, children }: FieldProps) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {!error && help && <p className="mt-1 text-xs text-gray-500">{help}</p>}
    </div>
  );
}

interface DateTimeRowProps {
  label: string;
  dateName: string;
  hourName: string;
  minuteName: string;
  dateValue: string;
  hourValue: string;
  minuteValue: string;
  onChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => void;
  disabled?: boolean;
}
function DateTimeRow({
  label,
  dateName,
  hourName,
  minuteName,
  dateValue,
  hourValue,
  minuteValue,
  onChange,
  disabled,
}: DateTimeRowProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <span className="w-10 text-xs font-medium text-gray-600">{label}</span>
      <TextInput
        type="date"
        name={dateName}
        value={dateValue}
        onChange={onChange}
        disabled={disabled}
        className="sm:w-44"
      />
      <div className="flex items-center gap-1">
        <Select
          name={hourName}
          value={hourValue}
          onChange={onChange}
          disabled={disabled}
          className="w-20"
        >
          {HOUR_OPTIONS.map(h => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </Select>
        <span className="text-sm text-gray-500">시</span>
        <Select
          name={minuteName}
          value={minuteValue}
          onChange={onChange}
          disabled={disabled}
          className="w-20"
        >
          {MINUTE_OPTIONS.map(m => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Select>
        <span className="text-sm text-gray-500">분</span>
      </div>
    </div>
  );
}
